import React, { useEffect, useMemo, useState, useCallback } from "react";
import { Link, useNavigate } from "react-router-dom";
import BodyWrapper from "../../../components/Wrapper/BodyWrapper";
import Header from "../../../components/Header/Header";
import { useDispatch, useSelector } from "react-redux";
import { fetchReport } from "../../../store/Slices/ReportSlice";
import FetchingLoading from "../../../components/Loaders/FetchingLoading";
import CustomBtn from "../../../components/Buttons/CustomBtn";
import MetricCard from "../../../components/Cards/MetricCard";
import { FaExchangeAlt } from "react-icons/fa";
import {
  MdPointOfSale,
  MdMoneyOff,
  MdPayments,
} from "react-icons/md";
import { buildReportSummary, fmtReportRs } from "../../../utils/reportSummary";
import { isAdminRole } from "../../../utils/appPaths";
import { usePageRefresh } from "../../../hooks/usePageRefresh";
import AddCashAdjustmentModal from "../../../components/Modals/AddCashAdjustmentModal";
import FinalizeClosingModal from "../../../components/Modals/FinalizeClosingModal";
import EditPaymentModal from "../../../components/Modals/EditPaymentModal";
import DeleteModal from "../../../components/Modals/DeleteModal";
import RowActionsMenu from "../../../components/RowActionsMenu/RowActionsMenu";
import {
  DeleteCustomerPaymentApi,
  DeleteSupplierPaymentApi,
} from "../../../ApiRequests";
import { ErrorToast, SuccessToast } from "../../../utils/ShowToast";
import ReportCashAdjustmentCardList from "./ReportCashAdjustmentCardList";
import ReportExpenseCardList from "./ReportExpenseCardList";
import ReportCompanyPaymentCardList from "./ReportCompanyPaymentCardList";
import ReportDateRangeBar, {
  localYmd,
} from "../../../components/Reports/ReportDateRangeBar";
import { formatDateTime } from "../../../utils/dateTime";

const resolveReportBranchId = (user = {}) => {
  const raw = user.branchId ?? user.branch;
  if (!raw) return localStorage.getItem("branch") || "";
  if (typeof raw === "object" && raw._id) return String(raw._id);
  return String(raw);
};

const fmtLedgerCell = (n) => {
  const value = Number(n || 0);
  if (!value) return "—";
  return fmtReportRs(value);
};

const isPaymentLedgerSource = (source) =>
  source === "customer_payment" || source === "company_payment";

const stripPaymentLedgerId = (id) =>
  String(id || "").replace(/^(cp|sp)-/, "");

const paymentTypeFromDetails = (payment, fallbackSource) => {
  if (payment?.user_type === "Company" || payment?.companyId || payment?.company_name) {
    return "Company";
  }
  if (payment?.user_type === "Customer" || payment?.customerId || payment?.customer_name) {
    return "Customer";
  }
  return fallbackSource === "company_payment" ? "Company" : "Customer";
};

const DailyReport = () => {
  const isHqAdmin = isAdminRole(); // Super Admin (0) + Admin (1) — same all-branches view
  const branchId = useMemo(() => {
    // HQ admins have no branch — empty branchId merges all branches cash/bank In/Out
    if (isHqAdmin) return "";
    const userData = JSON.parse(localStorage.getItem("user") || "{}");
    return resolveReportBranchId(userData);
  }, [isHqAdmin]);

  const dispatch = useDispatch();
  const ReportState = useSelector((state) => state.ReportState);
  const [SelectedDate, setSelectedDate] = useState(localYmd());
  const [SelectedDateTo, setSelectedDateTo] = useState(localYmd());
  const [openAdjustmentModal, setOpenAdjustmentModal] = useState(false);
  const [openFinalizeModal, setOpenFinalizeModal] = useState(false);
  const [showExpenseDetails, setShowExpenseDetails] = useState(false);
  const [showCompanyPaymentDetails, setShowCompanyPaymentDetails] = useState(false);
  const [expenseSearch, setExpenseSearch] = useState("");
  const [companyPaymentSearch, setCompanyPaymentSearch] = useState("");
  const [OpenEditPayment, setOpenEditPayment] = useState(false);
  const [OpenDeletePayment, setOpenDeletePayment] = useState(false);
  const [SelectedPayment, setSelectedPayment] = useState(null);
  const [SelectedPaymentType, setSelectedPaymentType] = useState("Customer");
  const [paymentActionLoading, setPaymentActionLoading] = useState(false);

  const basePath = window.location.pathname.startsWith("/branch")
    ? "/branch"
    : "/admin";
  const navigate = useNavigate();

  useEffect(() => {
    if (!SelectedDateTo || SelectedDateTo < SelectedDate) {
      setSelectedDateTo(SelectedDate);
    }
  }, [SelectedDate, SelectedDateTo]);

  useEffect(() => {
    setShowExpenseDetails(false);
    setShowCompanyPaymentDetails(false);
    setExpenseSearch("");
    setCompanyPaymentSearch("");
  }, [SelectedDate, SelectedDateTo]);

  useEffect(() => {
    const promise = dispatch(
      fetchReport({
        branchId,
        startDate: SelectedDate,
        endDate: SelectedDateTo || SelectedDate,
      }),
    );
    return () => {
      promise.abort?.();
    };
  }, [SelectedDate, SelectedDateTo, dispatch, branchId]);

  const refreshReport = useCallback(() => {
    dispatch(
      fetchReport({
        branchId,
        startDate: SelectedDate,
        endDate: SelectedDateTo || SelectedDate,
      }),
    );
  }, [dispatch, branchId, SelectedDate, SelectedDateTo]);

  const reportPayments = useMemo(() => {
    const list = ReportState?.data?.payments;
    return Array.isArray(list) ? list : [];
  }, [ReportState?.data?.payments]);

  const resolvePaymentDetails = useCallback(
    (row) => {
      if (!row) return null;
      const rawId = stripPaymentLedgerId(row._id);
      const fromPayments = reportPayments.find(
        (p) => String(p._id) === String(rawId)
      );
      if (fromPayments) return fromPayments;
      const companyList = ReportState?.data?.cash_company_payments;
      if (Array.isArray(companyList)) {
        const fromCompany = companyList.find(
          (p) => String(p._id) === String(rawId)
        );
        if (fromCompany) return fromCompany;
      }
      if (row.customerId || row.companyId || row.amount != null) {
        return { ...row, _id: rawId };
      }
      return null;
    },
    [reportPayments, ReportState?.data?.cash_company_payments]
  );

  const openEditPayment = useCallback(
    (row) => {
      const details = resolvePaymentDetails(row);
      if (!details?._id) {
        ErrorToast("Payment details not found");
        return;
      }
      setSelectedPayment(details);
      setSelectedPaymentType(
        paymentTypeFromDetails(details, row?.source)
      );
      setOpenEditPayment(true);
    },
    [resolvePaymentDetails]
  );

  const openDeletePayment = useCallback(
    (row) => {
      const details = resolvePaymentDetails(row);
      if (!details?._id) {
        ErrorToast("Payment details not found");
        return;
      }
      setSelectedPayment(details);
      setSelectedPaymentType(
        paymentTypeFromDetails(details, row?.source)
      );
      setOpenDeletePayment(true);
    },
    [resolvePaymentDetails]
  );

  const renderPaymentActionsCell = (row) => {
    if (!isPaymentLedgerSource(row?.source)) {
      return (
        <td className="border border-line px-2 py-2 text-center text-muted">
          —
        </td>
      );
    }
    return (
      <td className="border border-line px-2 py-2 text-center">
        <div className="flex justify-center">
          <RowActionsMenu
            row={row}
            onEdit={openEditPayment}
            onDelete={openDeletePayment}
          />
        </div>
      </td>
    );
  };

  usePageRefresh(refreshReport, [branchId, SelectedDate, SelectedDateTo]);

  const isInitialLoading = ReportState.loading && !ReportState.data;
  const isRefreshing = Boolean(ReportState.refreshing);

  const summary = useMemo(
    () => buildReportSummary(ReportState?.data),
    [ReportState?.data],
  );

  const cashAccount = useMemo(() => {
    const raw = ReportState?.data?.cash_account;
    const empty = {
      opening_balance: Number(summary.opening_cash || 0),
      total_in: Number(summary.cash_account_total_in || 0),
      total_out: Number(summary.cash_account_total_out || 0),
      net_amount: Number(summary.cash_account_net || 0),
      closing_balance: Number(summary.closing_cash || 0),
      guest_sales_in: 0,
      customer_payments_in: 0,
      saleEntries: [],
      otherEntries: [],
      entries: [],
    };
    if (!raw || typeof raw !== "object") return empty;

    const entries = Array.isArray(raw.entries) ? raw.entries : [];
    const byDate = (a, b) =>
      Number(a.date || 0) - Number(b.date || 0) ||
      String(a._id || "").localeCompare(String(b._id || ""));

    const isSaleIn = (row) =>
      row.source === "guest_invoice" || row.source === "customer_payment";

    const saleEntriesRaw = entries.filter(isSaleIn).sort(byDate);
    const otherEntriesRaw = entries.filter((row) => !isSaleIn(row)).sort(byDate);

    const opening_balance = Number(
      raw.opening_balance ?? summary.opening_cash ?? 0
    );
    // Keep backend `bal` — do not recompute on frontend
    const saleEntries = saleEntriesRaw.map((row) => ({
      ...row,
      bal: row.bal != null ? Number(row.bal) : null,
    }));
    const otherEntries = otherEntriesRaw.map((row) => ({
      ...row,
      bal: row.bal != null ? Number(row.bal) : null,
    }));
    const allEntries = [...saleEntries, ...otherEntries].sort(byDate);

    const guest_sales_in = saleEntries
      .filter((row) => row.source === "guest_invoice")
      .reduce((sum, row) => sum + Number(row.dr || 0), 0);
    const customer_payments_in = saleEntries
      .filter((row) => row.source === "customer_payment")
      .reduce((sum, row) => sum + Number(row.dr || 0), 0);

    const total_in = Number(raw.total_in || 0);
    const total_out = Number(raw.total_out || 0);

    return {
      opening_balance,
      total_in,
      total_out,
      net_amount: Number(raw.net_amount ?? total_in - total_out),
      closing_balance: Number(
        raw.closing_balance ?? summary.closing_cash ?? opening_balance
      ),
      guest_sales_in,
      customer_payments_in,
      saleEntries,
      otherEntries,
      entries: allEntries,
    };
  }, [ReportState?.data?.cash_account, summary]);

  const bankAccounts = useMemo(() => {
    const rows = ReportState?.data?.bank_accounts;
    if (!Array.isArray(rows)) return [];

    const byDate = (a, b) =>
      Number(a.date || 0) - Number(b.date || 0) ||
      String(a._id || "").localeCompare(String(b._id || ""));

    return rows.map((raw) => {
      const entries = (Array.isArray(raw.entries) ? raw.entries : [])
        .slice()
        .sort(byDate)
        .map((row) => ({
          ...row,
          // Keep backend `bal`
          bal: row.bal != null ? Number(row.bal) : null,
        }));
      const opening_balance = Number(raw.opening_balance || 0);
      const customer_payments_in = entries
        .filter((row) => row.source === "customer_payment")
        .reduce((sum, row) => sum + Number(row.dr || 0), 0);
      const total_in = Number(raw.total_in || 0);
      const total_out = Number(raw.total_out || 0);
      return {
        key: String(
          raw.account?._id || raw.account?.account_name || Math.random()
        ),
        name: raw.account?.account_name || "Bank",
        account_no: raw.account?.account_no,
        opening_balance,
        total_in,
        total_out,
        net_amount: Number(raw.net_amount ?? total_in - total_out),
        closing_balance: Number(
          raw.closing_balance ??
            (entries.length
              ? entries[entries.length - 1].bal
              : opening_balance)
        ),
        customer_payments_in,
        entries,
      };
    }).filter(
      (bank) =>
        bank.entries.length > 0 ||
        Number(bank.total_in) !== 0 ||
        Number(bank.total_out) !== 0
    );
  }, [ReportState?.data?.bank_accounts]);

  const [accountFilter, setAccountFilter] = useState("all"); // all | cash | bank

  const combinedAccounts = useMemo(() => {
    const byDate = (a, b) =>
      Number(a.date || 0) - Number(b.date || 0) ||
      String(a._id || "").localeCompare(String(b._id || ""));

    const openingSource =
      ReportState?.data?.cash_account?.opening_source ||
      summary.cash_opening_source ||
      "";
    const priorCloseDate =
      ReportState?.data?.cash_account?.prior_close_date ||
      summary.cash_prior_close_date ||
      null;
    // Opening only when a previous day was finalized (DayClose)
    const hasPriorClose =
      openingSource === "day_close" || Boolean(priorCloseDate);

    const cashEntries = (cashAccount.entries || []).map((row) => ({
      ...row,
      account_name: "CASH",
      account_kind: "cash",
    }));

    const bankEntries = bankAccounts.flatMap((bank) =>
      (bank.entries || []).map((row) => ({
        ...row,
        account_name: bank.name || "Bank",
        account_kind: "bank",
        // per-account running bal is bank-specific; hide in combined "all"
        bal: accountFilter === "bank" && bankAccounts.length === 1 ? row.bal : undefined,
      }))
    );

    const allEntries = [...cashEntries, ...bankEntries].sort(byDate);
    const filtered =
      accountFilter === "cash"
        ? cashEntries.sort(byDate)
        : accountFilter === "bank"
          ? bankEntries.sort(byDate)
          : allEntries;

    const total_in =
      accountFilter === "cash"
        ? Number(cashAccount.total_in || 0)
        : accountFilter === "bank"
          ? bankAccounts.reduce((s, b) => s + Number(b.total_in || 0), 0)
          : Number(cashAccount.total_in || 0) +
            bankAccounts.reduce((s, b) => s + Number(b.total_in || 0), 0);

    const total_out =
      accountFilter === "cash"
        ? Number(cashAccount.total_out || 0)
        : accountFilter === "bank"
          ? bankAccounts.reduce((s, b) => s + Number(b.total_out || 0), 0)
          : Number(cashAccount.total_out || 0) +
            bankAccounts.reduce((s, b) => s + Number(b.total_out || 0), 0);

    // Bank Transfer In → hide from In.
    // Cash Transfer In (Bank → Cash / any → Cash) → SHOW in In.
    const isTransferIn = (row) =>
      row.source === "cash_adjustment_in" ||
      String(row.type_label || "")
        .toLowerCase()
        .includes("transfer in");

    const isTransferOut = (row) =>
      row.source === "cash_adjustment_out" ||
      String(row.type_label || "")
        .toLowerCase()
        .includes("transfer out");

    const isCashName = (name) =>
      String(name || "")
        .trim()
        .toUpperCase() === "CASH";

    const hideTransferInFromIn = (row) =>
      isTransferIn(row) && row.account_kind !== "cash";

    // Bank → Cash: show only on Cash In, not Bank Out
    const isBankToCashTransferOut = (row) =>
      row.account_kind === "bank" &&
      isTransferOut(row) &&
      (isCashName(row.party) || isCashName(row.into_account));

    const customer_payments_in = filtered
      .filter(
        (row) =>
          row.source === "customer_payment" &&
          Number(row.dr || 0) > 0 &&
          !hideTransferInFromIn(row)
      )
      .reduce((s, row) => s + Number(row.dr || 0), 0);
    const guest_sales_in = filtered
      .filter(
        (row) =>
          row.account_kind === "cash" && row.source === "guest_invoice"
      )
      .reduce((s, row) => s + Number(row.dr || 0), 0);

    // Direct Loan (Loans page) paid from Bank — keep in Loans section only, not Cash & Bank Out/In
    const isBankDirectLoan = (row) =>
      row.account_kind === "bank" &&
      (row.source === "loan_given" ||
        row.source === "loan_taken" ||
        row.source === "loan_repayment_in" ||
        row.source === "loan_repayment_out" ||
        String(row.type_label || "")
          .toLowerCase()
          .includes("loan"));

    // Company payments from Bank — hide from Cash & Bank Out
    const isBankCompanyPayment = (row) =>
      row.account_kind === "bank" &&
      (row.source === "company_payment" ||
        String(row.type_label || "")
          .toLowerCase()
          .includes("company payment"));

    // In = Cash DR + Bank DR (Cash Transfer In from bank allowed; bank Transfer In hidden)
    const inEntries = filtered
      .filter(
        (row) =>
          Number(row.dr || 0) > 0 &&
          !hideTransferInFromIn(row) &&
          !isBankDirectLoan(row)
      )
      .sort(byDate);

    // Bank customer payments (customer → my bank) also list under Out for visibility.
    // Amount stays on DR for totals — do not count them in Out total.
    const isBankCustomerPayment = (row) =>
      row.account_kind === "bank" &&
      row.source === "customer_payment" &&
      Number(row.dr || 0) > 0;

    const outEntries = filtered
      .filter(
        (row) =>
          (Number(row.cr || 0) > 0 || isBankCustomerPayment(row)) &&
          !isBankDirectLoan(row) &&
          !isBankCompanyPayment(row) &&
          !isBankToCashTransferOut(row)
      )
      .sort(byDate);

    const listed_in = inEntries.reduce((s, row) => s + Number(row.dr || 0), 0);

    // Bank Customer Payment is DR on ledger but listed under Out → treat as CR (−).
    const outMovement = (row) => {
      if (
        row.account_kind === "bank" &&
        row.source === "customer_payment" &&
        Number(row.dr || 0) > 0
      ) {
        return -Number(row.dr || 0);
      }
      return Number(row.dr || 0) - Number(row.cr || 0);
    };

    // Real Out amounts (CR + Bank Customer Payment shown under Out)
    const listed_out = outEntries.reduce((s, row) => {
      if (
        row.account_kind === "bank" &&
        row.source === "customer_payment" &&
        Number(row.dr || 0) > 0
      ) {
        return s + Number(row.dr || 0);
      }
      return s + Number(row.cr || 0);
    }, 0);

    // Opening only from previous DayClose — otherwise start at 0 (display)
    const cash_opening = hasPriorClose
      ? Number(cashAccount.opening_balance || 0)
      : 0;
    const bank_opening = 0;
    const opening =
      accountFilter === "bank" ? 0 : cash_opening;

    // Continuous running balance: DR +, CR −
    // Bank · Customer Payment under Out → − (shown amount)
    let run = opening;
    const inEntriesWithBal = inEntries.map((row) => {
      run += Number(row.dr || 0) - Number(row.cr || 0);
      return { ...row, run_bal: run };
    });

    const outEntriesWithBal = outEntries.map((row) => {
      run += outMovement(row);
      return {
        ...row,
        run_bal: run,
        out_balance_skip: false,
      };
    });
    const outBal = run;

    const cash_closing = Number(cashAccount.closing_balance || 0);
    const bank_closing = bankAccounts.reduce(
      (s, b) => s + Number(b.closing_balance || 0),
      0
    );

    return {
      entries: filtered,
      inEntries: inEntriesWithBal,
      outEntries: outEntriesWithBal,
      total_in: listed_in,
      total_out: listed_out,
      net_amount: listed_in - listed_out,
      opening,
      has_prior_close: hasPriorClose,
      prior_close_date: priorCloseDate,
      out_till_final: outBal,
      customer_payments_in,
      guest_sales_in,
      cash_opening,
      cash_closing,
      bank_opening,
      bank_closing,
    };
  }, [cashAccount, bankAccounts, accountFilter, summary, ReportState?.data]);

  const cashAdjustments = useMemo(() => {
    const rows = ReportState?.data?.cash_adjustments;
    return Array.isArray(rows) ? rows : [];
  }, [ReportState?.data]);

  const cashLoans = useMemo(() => {
    const rows = ReportState?.data?.cash_loans;
    return Array.isArray(rows) ? rows : [];
  }, [ReportState?.data]);

  const cashLoanRepayments = useMemo(() => {
    const rows = ReportState?.data?.cash_loan_repayments;
    return Array.isArray(rows) ? rows : [];
  }, [ReportState?.data]);

  const cashMovementCount =
    cashAdjustments.length + cashLoans.length + cashLoanRepayments.length;

  const reportExpenses = useMemo(() => {
    const rows = ReportState?.data?.expenses;
    return Array.isArray(rows) ? rows : [];
  }, [ReportState?.data]);

  const cashCompanyPayments = useMemo(() => {
    const rows = ReportState?.data?.cash_company_payments;
    return Array.isArray(rows) ? rows : [];
  }, [ReportState?.data]);

  const openMetricDetail = (type) => {
    const qs = new URLSearchParams({
      type,
      from: SelectedDate,
      to: SelectedDateTo || SelectedDate,
    });
    if (branchId) qs.set("branchId", branchId);
    if (type === "sales") qs.set("sale", "all");
    if (type === "cash_in") qs.set("cin", "all");
    if (type === "cash_out") qs.set("cout", "all");
    navigate(`${basePath}/daily-report/detail?${qs.toString()}`);
  };

  const dateLabel =
    SelectedDate === SelectedDateTo
      ? SelectedDate
      : `${SelectedDate} → ${SelectedDateTo}`;

  const isSingleDayReport = SelectedDate === SelectedDateTo;
  const reportEndDate = SelectedDateTo || SelectedDate;

  const reloadReport = () => {
    dispatch(
      fetchReport({
        branchId,
        startDate: SelectedDate,
        endDate: reportEndDate,
      }),
    );
  };

  const adjustmentReportContext = {
    label: "Daily report period",
    dateMin: SelectedDate,
    dateMax: reportEndDate,
    fixedDate: isSingleDayReport ? SelectedDate : null,
    requireConfirm: true,
  };

  return (
    <BodyWrapper>
      <Header
        title="Daily Report"
        desc="Sales, cash in/out, loans, expenses, returns & guest naqdi"
      />

      <div className="mb-3">
        <ReportDateRangeBar
          fromDate={SelectedDate}
          toDate={SelectedDateTo}
          onFromChange={setSelectedDate}
          onToChange={setSelectedDateTo}
          refreshing={isRefreshing}
          periodLabel={
            isHqAdmin ? `${dateLabel} · All branches` : dateLabel
          }
        />
        {ReportState.isError && ReportState.data ? (
          <p className="mt-2 text-xs font-semibold text-danger">
            Date update failed — pehle wala data dikh raha hai. Dobara try karo.
          </p>
        ) : null}
      </div>

      <div className="w-full">
        {isInitialLoading ? (
          <div className="flex justify-center items-center py-16">
            <FetchingLoading />
          </div>
        ) : ReportState.isError && !ReportState.data ? (
          <div className="rounded-xl border border-danger/20 bg-dangerSoft px-4 py-8 text-center text-danger">
            Could not load report. Please try again.
          </div>
        ) : (
          <div
            className={`space-y-5 transition-opacity duration-200 ${
              isRefreshing ? "opacity-60" : "opacity-100"
            }`}
          >
            <div className="grid grid-cols-3 max1000:grid-cols-2 max680:grid-cols-1 gap-4">
              <MetricCard
                title="Total Sales"
                value={fmtReportRs(summary.total_sales)}
                subtitle={`Customer ${fmtReportRs(summary.customer_sales)} · Guest ${fmtReportRs(summary.guest_sales)} · ${summary.invoice_count} invoice${summary.invoice_count !== 1 ? "s" : ""}`}
                chipText="Open table"
                tone="brand"
                icon={MdPointOfSale}
                onClick={() => openMetricDetail("sales")}
              />
              <MetricCard
                title="Cash In"
                value={fmtReportRs(summary.money_in_total ?? summary.total_cash_in)}
                subtitle={`Cash ${fmtReportRs(summary.money_in_cash ?? 0)} · Bank ${fmtReportRs(summary.money_in_bank ?? summary.bank_received_from_customers ?? 0)} · incl. loans`}
                chipText="Open table"
                tone="success"
                icon={MdPayments}
                onClick={() => openMetricDetail("cash_in")}
              />
              <MetricCard
                title="Cash Out"
                value={fmtReportRs(summary.money_out_total ?? 0)}
                subtitle={`Cash ${fmtReportRs(summary.money_out_cash ?? 0)} · Bank ${fmtReportRs(summary.money_out_bank ?? 0)} · incl. loans & expenses`}
                chipText="Open table"
                tone="danger"
                icon={MdMoneyOff}
                onClick={() => openMetricDetail("cash_out")}
              />
            </div>

            <section className="rounded-xl border border-line bg-card shadow-soft overflow-hidden">
              <div className="px-4 py-3 border-b border-line bg-successSoft/80">
                <h2 className="text-base font-bold text-ink">
                  Cash & Bank accounts
                </h2>
                <p className="text-xs text-muted mt-0.5">
                  In = Cash + Bank (Bank → Cash Transfer In included) · Out =
                  Cash + Bank (Bank → Cash Out exclude)
                  {summary.cash_prior_close_date ? (
                    <span className="text-main font-semibold">
                      {" "}
                      · cash opening from close {summary.cash_prior_close_date}
                    </span>
                  ) : null}
                </p>
              </div>

              <div className="px-4 py-3 border-b border-line bg-subtle/50 flex flex-wrap gap-1.5">
                {[
                  { id: "all", label: "All" },
                  { id: "cash", label: "Cash" },
                  { id: "bank", label: "Bank" },
                ].map((t) => {
                  const active = accountFilter === t.id;
                  return (
                    <button
                      key={t.id}
                      type="button"
                      onClick={() => setAccountFilter(t.id)}
                      className={`h-9 px-3 rounded-lg text-xs font-bold font-quicksand transition-colors ${
                        active
                          ? "bg-main text-white"
                          : "bg-card border border-line text-inkSecondary hover:border-main/40 hover:text-main"
                      }`}
                    >
                      {t.label}
                    </button>
                  );
                })}
              </div>

              <div className="px-4 py-3 border-b border-line bg-subtle/70 flex flex-wrap gap-2 text-xs">
                {accountFilter !== "bank" && combinedAccounts.has_prior_close ? (
                  <>
                    <span className="rounded-lg bg-card border border-line px-3 py-2 font-semibold text-inkSecondary">
                      Cash opening: {fmtReportRs(combinedAccounts.cash_opening)}
                      {combinedAccounts.prior_close_date ? (
                        <span className="text-muted font-medium">
                          {" "}
                          (close {combinedAccounts.prior_close_date})
                        </span>
                      ) : null}
                    </span>
                    <span className="rounded-lg bg-card border border-main/30 px-3 py-2 font-semibold text-main">
                      Cash closing: {fmtReportRs(combinedAccounts.cash_closing)}
                    </span>
                  </>
                ) : accountFilter !== "bank" ? (
                  <span className="rounded-lg bg-card border border-line px-3 py-2 font-semibold text-muted">
                    Cash opening: — (pehle previous day finalize closing karo)
                  </span>
                ) : null}
                {accountFilter !== "cash" ? (
                  <span className="rounded-lg bg-card border border-main/30 px-3 py-2 font-semibold text-main">
                    Bank closing: {fmtReportRs(combinedAccounts.bank_closing)}
                  </span>
                ) : null}
                <span className="rounded-lg bg-successSoft border border-success/20 px-3 py-2 font-semibold text-success">
                  Guest sales: {fmtReportRs(combinedAccounts.guest_sales_in)}
                </span>
                <span className="rounded-lg bg-successSoft border border-success/20 px-3 py-2 font-semibold text-success">
                  Customer payments:{" "}
                  {fmtReportRs(combinedAccounts.customer_payments_in)}
                </span>
                <span className="rounded-lg bg-successSoft border border-success/20 px-3 py-2 font-semibold text-success">
                  Total DR (In): {fmtReportRs(combinedAccounts.total_in)}
                </span>
                <span className="rounded-lg bg-dangerSoft border border-danger/20 px-3 py-2 font-semibold text-danger">
                  Total CR (Out): {fmtReportRs(combinedAccounts.total_out)}
                </span>
                <span
                  className={`rounded-lg border px-3 py-2 font-semibold ${
                    Number(combinedAccounts.net_amount) >= 0
                      ? "bg-successSoft border-success/20 text-success"
                      : "bg-dangerSoft border-danger/20 text-danger"
                  }`}
                >
                  Net: {fmtReportRs(combinedAccounts.net_amount)}
                </span>
              </div>

              <div className="overflow-x-auto">
                <table className="w-full text-sm border-collapse">
                  <thead>
                    <tr className="bg-main text-white">
                      <th className="border border-main px-3 py-2.5 text-left font-bold">
                        Date
                      </th>
                      <th className="border border-main px-3 py-2.5 text-left font-bold">
                        Into / From A/C
                      </th>
                      <th className="border border-main px-3 py-2.5 text-left font-bold">
                        Type
                      </th>
                      <th className="border border-main px-3 py-2.5 text-left font-bold">
                        Party
                      </th>
                      <th className="border border-main px-3 py-2.5 text-left font-bold">
                        Ref
                      </th>
                      <th className="border border-main px-3 py-2.5 text-left font-bold">
                        Description
                      </th>
                      <th className="border border-main px-3 py-2.5 text-right font-bold">
                        DR (In)
                      </th>
                      <th className="border border-main px-3 py-2.5 text-right font-bold">
                        CR (Out)
                      </th>
                      <th className="border border-main px-3 py-2.5 text-right font-bold">
                        Balance
                      </th>
                      <th className="border border-main px-3 py-2.5 text-center font-bold">
                        Actions
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {combinedAccounts.inEntries.length === 0 &&
                    combinedAccounts.outEntries.length === 0 ? (
                      <tr>
                        <td
                          colSpan={10}
                          className="border border-line px-3 py-8 text-center text-muted"
                        >
                          No Cash/Bank CR/DR entries for {dateLabel}.
                        </td>
                      </tr>
                    ) : (
                      <>
                        {combinedAccounts.has_prior_close &&
                        accountFilter !== "bank" ? (
                          <tr className="bg-subtle font-quicksand">
                            <td
                              colSpan={6}
                              className="border border-line px-3 py-2.5 font-bold text-ink"
                            >
                              Opening — Cash
                              {combinedAccounts.prior_close_date ? (
                                <span className="text-muted font-semibold text-xs ml-2">
                                  (from close {combinedAccounts.prior_close_date})
                                </span>
                              ) : null}
                            </td>
                            <td className="border border-line px-3 py-2.5 text-right tabular-nums text-muted">
                              —
                            </td>
                            <td className="border border-line px-3 py-2.5 text-right tabular-nums text-muted">
                              —
                            </td>
                            <td className="border border-line px-3 py-2.5 text-right tabular-nums font-extrabold text-main">
                              {fmtReportRs(combinedAccounts.opening)}
                            </td>
                            <td className="border border-line px-2 py-2" />
                          </tr>
                        ) : null}

                        <tr className="bg-successSoft/60 font-quicksand">
                          <td
                            colSpan={10}
                            className="border border-line px-3 py-2 text-xs font-bold uppercase tracking-wide text-success"
                          >
                            1. In (DR) — Cash + Bank
                          </td>
                        </tr>

                        {combinedAccounts.inEntries.length === 0 ? (
                          <tr>
                            <td
                              colSpan={10}
                              className="border border-line px-3 py-4 text-center text-muted text-sm"
                            >
                              Is period mein koi In entry nahi.
                            </td>
                          </tr>
                        ) : (
                          combinedAccounts.inEntries.map((row) => (
                            <tr
                              key={`in-${row.account_kind}-${row._id}`}
                              className="font-quicksand hover:bg-subtle/60 transition-colors"
                            >
                              <td className="border border-line px-3 py-2 whitespace-nowrap text-inkSecondary">
                                {row.n_date}
                              </td>
                              <td className="border border-line px-3 py-2 whitespace-nowrap">
                                <span
                                  className={`text-xs font-bold px-2 py-0.5 rounded-md ${
                                    row.account_kind === "cash"
                                      ? "bg-brandSoft text-main"
                                      : "bg-infoSoft text-info"
                                  }`}
                                >
                                  Into ·{" "}
                                  {row.into_account ||
                                    row.ledger_account ||
                                    row.account_name ||
                                    "—"}
                                </span>
                              </td>
                              <td className="border border-line px-3 py-2 whitespace-nowrap text-inkSecondary">
                                <span
                                  className={`text-xs font-bold px-2 py-0.5 rounded-md ${
                                    row.account_kind === "bank"
                                      ? "bg-infoSoft text-info"
                                      : "bg-subtle text-inkSecondary"
                                  }`}
                                >
                                  {row.account_kind === "bank"
                                    ? "Bank"
                                    : "Cash"}
                                  {" · "}
                                  {row.type_label ||
                                    String(row.source || "")
                                      .replace(/_/g, " ") ||
                                    "—"}
                                </span>
                              </td>
                              <td className="border border-line px-3 py-2 text-ink">
                                {row.party}
                              </td>
                              <td className="border border-line px-3 py-2 text-inkSecondary">
                                {row.reference}
                              </td>
                              <td className="border border-line px-3 py-2 max-w-[280px] text-inkSecondary">
                                {row.desc}
                              </td>
                              <td className="border border-line px-3 py-2 text-right tabular-nums text-success font-semibold">
                                {fmtLedgerCell(row.dr)}
                              </td>
                              <td className="border border-line px-3 py-2 text-right tabular-nums text-muted">
                                —
                              </td>
                              <td className="border border-line px-3 py-2 text-right tabular-nums font-bold text-ink">
                                {row.run_bal != null
                                  ? fmtReportRs(row.run_bal)
                                  : "—"}
                              </td>
                              {renderPaymentActionsCell(row)}
                            </tr>
                          ))
                        )}

                        <tr className="bg-successSoft/40 font-quicksand">
                          <td
                            colSpan={6}
                            className="border border-line px-3 py-2.5 font-bold text-success"
                          >
                            Total In (DR) — Cash + Bank
                          </td>
                          <td className="border border-line px-3 py-2.5 text-right tabular-nums font-extrabold text-success">
                            {fmtReportRs(combinedAccounts.total_in)}
                          </td>
                          <td className="border border-line px-3 py-2.5 text-right tabular-nums text-muted">
                            —
                          </td>
                          <td className="border border-line px-3 py-2.5 text-right tabular-nums font-extrabold text-ink">
                            {(() => {
                              const last =
                                combinedAccounts.inEntries[
                                  combinedAccounts.inEntries.length - 1
                                ];
                              return last?.run_bal != null
                                ? fmtReportRs(last.run_bal)
                                : "—";
                            })()}
                          </td>
                          <td className="border border-line px-2 py-2" />
                        </tr>

                        <tr className="bg-dangerSoft/60 font-quicksand">
                          <td
                            colSpan={10}
                            className="border border-line px-3 py-2 text-xs font-bold uppercase tracking-wide text-danger"
                          >
                            2. Out (CR) — Cash + Bank
                          </td>
                        </tr>

                        {combinedAccounts.outEntries.length === 0 ? (
                          <tr>
                            <td
                              colSpan={10}
                              className="border border-line px-3 py-4 text-center text-muted text-sm"
                            >
                              Is period mein koi Out entry nahi.
                            </td>
                          </tr>
                        ) : (
                          combinedAccounts.outEntries.map((row) => {
                            const isBankCustomerPay =
                              row.account_kind === "bank" &&
                              row.source === "customer_payment" &&
                              Number(row.dr || 0) > 0;
                            const outAmount = isBankCustomerPay
                              ? Number(row.dr || 0)
                              : Number(row.cr || 0);
                            return (
                            <tr
                              key={`out-${row.account_kind}-${row._id}`}
                              className="font-quicksand hover:bg-subtle/60 transition-colors"
                            >
                              <td className="border border-line px-3 py-2 whitespace-nowrap text-inkSecondary">
                                {row.n_date || formatDateTime(row.date)}
                              </td>
                              <td className="border border-line px-3 py-2 whitespace-nowrap">
                                <span
                                  className={`text-xs font-bold px-2 py-0.5 rounded-md ${
                                    row.account_kind === "cash"
                                      ? "bg-brandSoft text-main"
                                      : "bg-infoSoft text-info"
                                  }`}
                                >
                                  {isBankCustomerPay ? "Into · " : "From · "}
                                  {isBankCustomerPay
                                    ? row.into_account ||
                                      row.ledger_account ||
                                      row.account_name ||
                                      "—"
                                    : row.from_account ||
                                      row.ledger_account ||
                                      row.account_name ||
                                      "—"}
                                </span>
                              </td>
                              <td className="border border-line px-3 py-2 whitespace-nowrap text-inkSecondary">
                                <span
                                  className={`text-xs font-bold px-2 py-0.5 rounded-md ${
                                    row.account_kind === "bank"
                                      ? "bg-infoSoft text-info"
                                      : "bg-subtle text-inkSecondary"
                                  }`}
                                >
                                  {row.account_kind === "bank"
                                    ? "Bank"
                                    : "Cash"}
                                  {" · "}
                                  {row.type_label ||
                                    String(row.source || "")
                                      .replace(/_/g, " ") ||
                                    "—"}
                                </span>
                              </td>
                              <td className="border border-line px-3 py-2 text-ink">
                                {row.party || "—"}
                              </td>
                              <td className="border border-line px-3 py-2 text-inkSecondary">
                                {row.reference || "—"}
                              </td>
                              <td className="border border-line px-3 py-2 max-w-[280px] text-inkSecondary">
                                {row.desc || "—"}
                              </td>
                              <td className="border border-line px-3 py-2 text-right tabular-nums text-muted">
                                —
                              </td>
                              <td
                                className={`border border-line px-3 py-2 text-right tabular-nums font-semibold ${
                                  isBankCustomerPay
                                    ? "text-success"
                                    : row.out_balance_skip
                                      ? "text-muted"
                                      : "text-danger"
                                }`}
                              >
                                {fmtLedgerCell(outAmount)}
                              </td>
                              <td
                                className={`border border-line px-3 py-2 text-right tabular-nums font-bold ${
                                  row.out_balance_skip
                                    ? "text-muted"
                                    : "text-ink"
                                }`}
                                title={
                                  row.out_balance_skip
                                    ? "Visibility only — balance unchanged"
                                    : undefined
                                }
                              >
                                {row.run_bal != null
                                  ? fmtReportRs(row.run_bal)
                                  : "—"}
                              </td>
                              {renderPaymentActionsCell(row)}
                            </tr>
                            );
                          })
                        )}

                        <tr className="bg-dangerSoft/40 font-quicksand">
                          <td
                            colSpan={6}
                            className="border border-line px-3 py-2.5 font-bold text-danger"
                          >
                            Total Out (CR)
                            {accountFilter === "all"
                              ? " — Cash + Bank"
                              : accountFilter === "cash"
                                ? " — Cash"
                                : " — Bank"}
                          </td>
                          <td className="border border-line px-3 py-2.5 text-right tabular-nums text-muted">
                            —
                          </td>
                          <td className="border border-line px-3 py-2.5 text-right tabular-nums font-extrabold text-danger">
                            {fmtReportRs(combinedAccounts.total_out)}
                          </td>
                          <td className="border border-line px-3 py-2.5 text-right tabular-nums font-extrabold text-ink">
                            {fmtReportRs(combinedAccounts.out_till_final)}
                          </td>
                          <td className="border border-line px-2 py-2" />
                        </tr>
                      </>
                    )}

                    <tr className="bg-main/5 font-quicksand">
                      <td
                        colSpan={6}
                        className="border border-line px-3 py-3 font-extrabold text-ink"
                      >
                        Net (Total In − Total Out)
                      </td>
                      <td
                        colSpan={2}
                        className={`border border-line px-3 py-3 text-right tabular-nums font-extrabold text-base ${
                          Number(combinedAccounts.net_amount) >= 0
                            ? "text-success"
                            : "text-danger"
                        }`}
                      >
                        {fmtReportRs(combinedAccounts.net_amount)}
                      </td>
                      <td className="border border-line px-3 py-3 text-right tabular-nums font-extrabold text-main text-base">
                        {fmtReportRs(combinedAccounts.out_till_final)}
                      </td>
                      <td className="border border-line px-2 py-2" />
                    </tr>
                  </tbody>
                </table>
              </div>
            </section>

            <section className="rounded-xl border border-danger/20 bg-card shadow-soft overflow-hidden">
              <div className="px-4 py-3 border-b border-danger/20 bg-dangerSoft/80 flex flex-wrap items-center justify-between gap-2">
                <div>
                  <h2 className="text-base font-bold text-ink">
                    Detailed Expenses
                  </h2>
                  <p className="text-xs text-muted mt-0.5">
                    {dateLabel} · only CASH account affects drawer
                  </p>
                </div>
                <div className="flex flex-wrap items-center gap-2">
                  <span className="text-xs font-bold px-2.5 py-1 rounded-full bg-dangerSoft text-danger">
                    {fmtReportRs(summary.total_expenses)} ·{" "}
                    {summary.expense_count ?? 0} item
                    {(summary.expense_count ?? 0) !== 1 ? "s" : ""}
                  </span>
                  <CustomBtn
                    title={showExpenseDetails ? "Hide" : "Show"}
                    onClick={() => setShowExpenseDetails((open) => !open)}
                  />
                </div>
              </div>
              {showExpenseDetails && (
                <div className="p-4">
                  <ReportExpenseCardList
                    expenses={reportExpenses}
                    searchText={expenseSearch}
                    setSearchText={setExpenseSearch}
                    emptyMessage={`No expenses for ${dateLabel}.`}
                    paginate={false}
                  />
                </div>
              )}
            </section>

            <section className="rounded-xl border border-line bg-card shadow-soft overflow-hidden">
              <div className="px-4 py-3 border-b border-line bg-subtle/80 flex flex-wrap items-center justify-between gap-2">
                <div>
                  <h2 className="text-base font-bold text-ink">
                    Company payments (cash)
                  </h2>
                  <p className="text-xs text-muted mt-0.5">
                    {dateLabel} · paid from CASH account only
                  </p>
                </div>
                <div className="flex flex-wrap items-center gap-2">
                  <span className="text-xs font-bold px-2.5 py-1 rounded-full bg-subtle text-ink">
                    {fmtReportRs(summary.cash_paid_to_companies)} ·{" "}
                    {summary.company_cash_payment_count ?? 0} payment
                    {(summary.company_cash_payment_count ?? 0) !== 1 ? "s" : ""}
                  </span>
                  <CustomBtn
                    title={showCompanyPaymentDetails ? "Hide" : "Show"}
                    onClick={() =>
                      setShowCompanyPaymentDetails((open) => !open)
                    }
                  />
                </div>
              </div>
              {showCompanyPaymentDetails && (
                <div className="p-4">
                  <ReportCompanyPaymentCardList
                    payments={cashCompanyPayments}
                    searchText={companyPaymentSearch}
                    setSearchText={setCompanyPaymentSearch}
                    emptyMessage={`No cash company payments for ${dateLabel}.`}
                    paginate={false}
                    onEditPayment={openEditPayment}
                    onDeletePayment={openDeletePayment}
                  />
                </div>
              )}
            </section>

            <section className="rounded-xl border border-line bg-card shadow-soft overflow-hidden">
              <div className="px-4 py-3 border-b border-line bg-subtle/90 flex flex-wrap items-center justify-between gap-2">
                <div>
                  <h2 className="text-base font-bold text-ink flex items-center gap-2">
                    <FaExchangeAlt className="text-muted" />
                    Cash adjustments & loans
                  </h2>
                  <p className="text-xs text-muted mt-0.5">
                    Transfers and CASH loans for{" "}
                    <span className="font-semibold text-inkSecondary">
                      {dateLabel}
                    </span>
                  </p>
                </div>
                <div className="flex flex-wrap items-center gap-2">
                  <span className="text-xs font-bold px-2.5 py-1 rounded-full bg-subtle/80 text-inkSecondary">
                    {cashMovementCount} record
                    {cashMovementCount !== 1 ? "s" : ""}
                  </span>
                  <CustomBtn
                    title="Add adjustment"
                    onClick={() => setOpenAdjustmentModal(true)}
                  />
                </div>
              </div>

              {cashMovementCount === 0 ? (
                <div className="px-4 py-8 text-center text-sm text-muted">
                  No cash adjustments or CASH loans for this date range.{" "}
                  <button
                    type="button"
                    onClick={() => setOpenAdjustmentModal(true)}
                    className="font-semibold text-main hover:underline"
                  >
                    Add adjustment
                  </button>{" "}
                  for <strong>{dateLabel}</strong>, or use{" "}
                  <Link
                    to={`${basePath}/finance`}
                    className="font-semibold text-main hover:underline"
                  >
                    Finance → Cash Adjustment
                  </Link>
                  {" / "}
                  <Link
                    to={`${basePath}/loans`}
                    className="font-semibold text-main hover:underline"
                  >
                    Loans
                  </Link>
                  .
                </div>
              ) : (
                <ReportCashAdjustmentCardList
                  adjustments={cashAdjustments}
                  cashLoans={cashLoans}
                  cashLoanRepayments={cashLoanRepayments}
                  summary={summary}
                  onDeleted={refreshReport}
                />
              )}
            </section>

            <section className="rounded-xl border border-main/25 bg-card shadow-soft overflow-hidden">
              <div className="px-4 py-4 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
                <div>
                  <h2 className="text-base font-bold text-ink">
                    Finalize closing
                  </h2>
                  <p className="text-xs text-muted mt-0.5">
                    {isSingleDayReport ? (
                      <>
                        Save CASH closing for{" "}
                        <span className="font-semibold text-inkSecondary">
                          {SelectedDate}
                        </span>{" "}
                        as next day&apos;s opening (
                        {fmtReportRs(cashAccount.closing_balance)})
                        {summary.day_closed ? (
                          <span className="text-success font-semibold">
                            {" "}
                            · already finalized
                          </span>
                        ) : null}
                      </>
                    ) : (
                      "Select a single day (From = To) to finalize closing."
                    )}
                  </p>
                </div>
                <button
                  type="button"
                  disabled={!isSingleDayReport || !branchId}
                  onClick={() => setOpenFinalizeModal(true)}
                  className="inline-flex items-center justify-center h-11 px-5 rounded-lg border-2 border-main bg-main text-white text-sm font-bold font-quicksand hover:bg-mainHover disabled:opacity-50 disabled:cursor-not-allowed whitespace-nowrap"
                >
                  {summary.day_closed ? "Update finalize closing" : "Finalize closing"}
                </button>
              </div>
            </section>

            <p className="text-sm text-muted">
              Need invoice lists, returns, or builti details?{" "}
              <Link
                to={`${basePath}/report`}
                className="font-semibold text-main hover:underline"
              >
                Open full Reports
              </Link>
            </p>
          </div>
        )}
      </div>

      {openAdjustmentModal && (
        <AddCashAdjustmentModal
          open={openAdjustmentModal}
          onClose={() => setOpenAdjustmentModal(false)}
          onSubmit={reloadReport}
          reportContext={adjustmentReportContext}
        />
      )}

      {openFinalizeModal && (
        <FinalizeClosingModal
          open={openFinalizeModal}
          onClose={() => setOpenFinalizeModal(false)}
          onSuccess={reloadReport}
          branchId={branchId}
          closeDate={SelectedDate}
          openingBalance={cashAccount.opening_balance}
          closingBalance={cashAccount.closing_balance}
          totalIn={cashAccount.total_in}
          totalOut={cashAccount.total_out}
        />
      )}

      {OpenEditPayment && SelectedPayment && (
        <EditPaymentModal
          Open={OpenEditPayment}
          setOpen={setOpenEditPayment}
          paymentDetails={SelectedPayment}
          type={SelectedPaymentType}
          onSuccess={refreshReport}
        />
      )}

      {OpenDeletePayment && SelectedPayment && (
        <DeleteModal
          Open={OpenDeletePayment}
          setOpen={setOpenDeletePayment}
          Loading={paymentActionLoading}
          onSubmit={async () => {
            setPaymentActionLoading(true);
            try {
              const isCompany = SelectedPaymentType === "Company";
              const response = isCompany
                ? await DeleteSupplierPaymentApi(SelectedPayment._id)
                : await DeleteCustomerPaymentApi(SelectedPayment._id);
              if (response.data.success) {
                setOpenDeletePayment(false);
                SuccessToast(
                  isCompany
                    ? "Company payment deleted successfully"
                    : "Customer payment deleted successfully"
                );
                refreshReport();
              } else {
                ErrorToast(response.data.error?.msg);
              }
            } catch (err) {
              ErrorToast(
                err?.response?.data?.error?.msg || "Failed to delete payment"
              );
            }
            setPaymentActionLoading(false);
          }}
        />
      )}
    </BodyWrapper>
  );
};

export default DailyReport;
